import { TonoEstado } from "../components/kit/EstadoChip";
import { EstadoPostulacion } from "../types/postulacion";
import { EstadoTema, MiPostulacion } from "../types/tema";

/**
 * Traduce los estados del dominio a los tonos del Design Kit. Es el único punto
 * donde se decide "qué color tiene este estado": los componentes solo reciben
 * un tono ya resuelto.
 */
export function tonoDeTema(estado: EstadoTema | undefined): TonoEstado {
  switch (estado) {
    case "disponible":
      return "exito";
    case "asignado":
      return "neutro";
    case "pausado": // el guía lo guardó: sigue siendo suyo, pero fuera del catálogo
      return "aviso";
    case "revision": // "No disponible" — aún en revisión del coordinador
      return "info";
    case "rechazado": // propuesta que el guía rechazó (soft delete)
      return "error";
    default:
      return "neutro";
  }
}

export function tonoDePostulacion(estado: EstadoPostulacion): TonoEstado {
  switch (estado) {
    case "aceptada":
      return "exito";
    case "rechazada":
      return "error";
    case "pendiente":
      return "aviso";
    // Revocada no es un rechazo: el tema se le dio y después se le quitó. Va en
    // neutro para no leerse como "te dijeron que no".
    case "revocada":
      return "neutro";
  }
}

/** La postulación aceptada lleva ✓ en vez del punto (estado "Aprobado" del kit). */
export const iconoDePostulacion = (estado: EstadoPostulacion) => (estado === "aceptada" ? ("check" as const) : undefined);

/**
 * Chip de estado de un tema, desde el punto de vista de quien mira.
 *
 * El estado interno del tema no le sirve al estudiante que postuló: "Asignado" no
 * dice si se lo dieron a él o a otro, y "Disponible" no dice que a él lo
 * rechazaron. Así que si tiene algo que ver con el tema, el chip habla de eso; si
 * no, del estado del tema.
 */
export function chipDeTema(tema: {
  estado: string;
  estado_codigo: EstadoTema;
  mi_postulacion?: MiPostulacion | null;
  es_mi_tema?: boolean;
}): { label: string; tono: TonoEstado; icono?: "check" } {
  if (tema.mi_postulacion?.estado === "pendiente") {
    return { label: "Postulación pendiente", tono: tonoDePostulacion("pendiente") };
  }
  // Suyo por partida doble: se lo aceptaron, o la coordinación se lo asignó.
  if (tema.es_mi_tema || tema.mi_postulacion?.estado === "aceptada") {
    return { label: "Tu tema", tono: "exito", icono: "check" };
  }
  if (tema.mi_postulacion?.estado === "rechazada") {
    return { label: "Postulación rechazada", tono: tonoDePostulacion("rechazada") };
  }
  if (tema.mi_postulacion?.estado === "revocada") {
    return { label: "Tema revocado", tono: tonoDePostulacion("revocada") };
  }
  return { label: tema.estado, tono: tonoDeTema(tema.estado_codigo) };
}

/**
 * A quien mira se le revocó este tema: lo tuvo y la coordinación se lo quitó.
 *
 * Es por espectador, no del tema: `mi_postulacion` es la de quien pregunta. Lo usan
 * las vistas para dejar la tarjeta inerte —el backend ya no le deja volver a
 * postular (ver `_postulable`), así que abrirla no lleva a ninguna parte—.
 */
export const temaRevocadoParaMi = (tema: { mi_postulacion?: MiPostulacion | null }) => tema.mi_postulacion?.estado === "revocada";

/**
 * Cómo se pinta el estado de un proceso. **Un solo lugar**, para que la lista de
 * estudiantes y la ficha de uno no se contradigan.
 *
 * Los desenlaces van rellenos y lo que está de paso, dibujado: entregado es el mismo
 * azul que "en evaluación" pero sin relleno —es el paso anterior del mismo camino— y
 * observación, gris dibujado, que es un alto y no un resultado. Así ninguno se
 * confunde con reprobado, que es el único rojo.
 */
export type EstiloEstado = { tono: TonoEstado; contorno?: boolean };

const ESTILOS: Record<string, EstiloEstado> = {
  aprobado: { tono: "exito" },
  reprobado: { tono: "error" },
  observacion: { tono: "neutro", contorno: true },
  en_evaluacion: { tono: "info" },
  entregado: { tono: "info", contorno: true },
  pendiente: { tono: "aviso" },
  eliminado: { tono: "neutro" },
};

/** Estado de una InstanciaPaso, por código. */
export function estiloDeInstancia(estado: string | undefined): EstiloEstado {
  return ESTILOS[estado ?? ""] ?? { tono: "aviso" };
}

/**
 * Lo mismo desde la etiqueta legible: el resumen del estudiante
 * (`ultima_instancia`) trae "En evaluación" y no el código.
 */
export function estiloDeInstanciaTexto(estado: string | undefined): EstiloEstado {
  if (!estado) return { tono: "neutro" };
  const e = estado.toLowerCase();
  if (e.includes("observ")) return ESTILOS.observacion;
  if (e.includes("aprob")) return ESTILOS.aprobado;
  if (e.includes("reprob") || e.includes("rechaz")) return ESTILOS.reprobado;
  if (e.includes("evalu")) return ESTILOS.en_evaluacion;
  if (e.includes("entreg")) return ESTILOS.entregado;
  return ESTILOS.pendiente;
}

/** ✓ en vez del punto cuando el proceso está aprobado (kit §04). */
export const iconoDeInstancia = (estado: string) => (estado === "aprobado" ? ("check" as const) : undefined);
